import { lazy } from 'react'
import { createBrowserRouter } from 'react-router-dom'
import App from '../App'
import ProtectedRoute from './ProtectedRoute'
import ROLE from '../common/role'

// Every route page is lazy-loaded so the initial JS bundle only ships what's needed for
// the first paint. The actual <Suspense> boundary lives in App.js around <Outlet/> so a
// single shared fallback covers every route. Pages used by ProtectedRoute (which is a
// wrapper, not a route element) and App itself stay eager.
const Home              = lazy(() => import('../pages/Home'))
const Login             = lazy(() => import('../pages/Login'))
const ForgotPassword    = lazy(() => import('../pages/ForgotPassword'))
const OtpVerification   = lazy(() => import('../pages/OtpVerification'))
const ResetPassword     = lazy(() => import('../pages/ResetPassword'))
const SignUp            = lazy(() => import('../pages/SignUp'))
const AdminPanel        = lazy(() => import('../pages/AdminPanel'))
const BrandAdmin        = lazy(() => import('../pages/BrandAdmin'))
const SupplierAdmin     = lazy(() => import('../pages/SupplierAdmin'))
const AllUsers          = lazy(() => import('../pages/AllUsers'))
const AllProducts       = lazy(() => import('../pages/AllProducts'))
const AllOrders         = lazy(() => import('../pages/AllOrders'))
const CategoryProduct   = lazy(() => import('../pages/CategoryProduct'))
const ProductDetails    = lazy(() => import('../pages/ProductDetails'))
const AdminAnalytics    = lazy(() => import('../pages/AdminAnalytics'))
const AdminBanners      = lazy(() => import('../pages/AdminBanners'))
const Cart              = lazy(() => import('../pages/Cart'))
const Checkout          = lazy(() => import('../pages/Checkout'))
const Payment           = lazy(() => import('../pages/Payment'))
const PaymentSuccess    = lazy(() => import('../pages/PaymentSuccess'))
const SearchProduct     = lazy(() => import('../pages/SearchProduct'))
const CheckoutComplete  = lazy(() => import('../pages/CheckoutComplete'))
const AddressDemo       = lazy(() => import('../pages/AddressDemo'))
const AuditTrail        = lazy(() => import('../pages/AuditTrail'))
const SmsLog            = lazy(() => import('../pages/SmsLog'))
const MyOrders          = lazy(() => import('../pages/MyOrders'))
const OrderLookup       = lazy(() => import('../pages/OrderLookup'))
const OrderDetails      = lazy(() => import('../pages/OrderDetails'))
const PublicOrderDetails = lazy(() => import('../pages/PublicOrderDetails'))
const MapsDiagnostics   = lazy(() => import('../pages/MapsDiagnostics'))
const NotFound          = lazy(() => import('../pages/NotFound'))
const AdminStoreSettings = lazy(() => import('../pages/AdminStoreSettings'))
const StoreDeliveryMap  = lazy(() => import('../pages/StoreDeliveryMap'))
const TicketScanner     = lazy(() => import('../verticals/events/pages/TicketScanner'))
const TicketStatus      = lazy(() => import('../verticals/events/pages/TicketStatus'))
const Wishlist          = lazy(() => import('../pages/Wishlist'))
const AdminDeliveryJobs = lazy(() => import('../pages/AdminDeliveryJobs'))
const PublicDeliveryTrack = lazy(() => import('../pages/PublicDeliveryTrack'))
const Events            = lazy(() => import('../verticals/events/pages/Events'))
const Groceries         = lazy(() => import('../verticals/groceries/pages/Groceries'))
const Fashion           = lazy(() => import('../verticals/fashion/pages/Fashion'))
const Preorders          = lazy(() => import('../pages/Preorders'))
const AdminPreorders     = lazy(() => import('../pages/AdminPreorders'))
const Electronics       = lazy(() => import('../verticals/electronics/pages/Electronics'))
const DepartmentDashboard = lazy(() => import('../pages/DepartmentDashboard'))
const WarrantyAging = lazy(() => import('../verticals/electronics/pages/WarrantyAging'))
const GroceryBatches = lazy(() => import('../verticals/groceries/pages/GroceryBatches'))
const FashionReplenishment = lazy(() => import('../verticals/fashion/pages/FashionReplenishment'))
const AttendeesMaster    = lazy(() => import('../pages/AttendeesMaster'))
const StockBackSubscriptions = lazy(() => import('../pages/StockBackSubscriptions'))
const CatalogManagement = lazy(() => import('../pages/CatalogManagement'))
const CategoryTreeAdmin = lazy(() => import('../pages/CategoryTreeAdmin'))
const SkuMaster         = lazy(() => import('../pages/SkuMaster'))
const InventoryDashboard = lazy(() => import('../pages/InventoryDashboard'))
const MovementsFeed     = lazy(() => import('../pages/MovementsFeed'))
const PricingMatrix     = lazy(() => import('../pages/PricingMatrix'))
const StocktakeList     = lazy(() => import('../pages/StocktakeList'))
const StocktakeSession  = lazy(() => import('../pages/StocktakeSession'))
const BecomeVendor      = lazy(() => import('../pages/BecomeVendor'))
const VendorPortal      = lazy(() => import('../pages/VendorPortal'))
const AdminVendors      = lazy(() => import('../pages/AdminVendors'))

// Role groups for the admin shell. Sub-routes that require SUPER_ADMIN specifically
// (audit trail) use their own narrower gate.
const ADMIN_ROLES = [ROLE.SUPER_ADMIN, ROLE.ADMIN, ROLE.SUB_ADMIN]
const SUPER_ADMIN_ONLY = [ROLE.SUPER_ADMIN]

const router = createBrowserRouter([
    {
        path : "/",
        element : <App/>,
        children : [
            { index: true,                              element: <Home/> },
            { path: "login",                            element: <Login/> },
            { path: "forgot-password",                  element: <ForgotPassword/> },
            { path: "reset-password",                   element: <ResetPassword/> },
            { path: "verification-otp",                 element: <OtpVerification/> },
            { path: "sign-up",                          element: <SignUp/> },
            { path: "product-category",                 element: <CategoryProduct/> },
            { path: "product/:id",                      element: <ProductDetails/> },
            { path: "events",                           element: <Events/> },
            { path: "groceries",                        element: <Groceries/> },
            { path: "fashion",                          element: <Fashion/> },
            { path: "electronics",                      element: <Electronics/> },
            { path: "preorders",                        element: <Preorders/> },
            { path: 'cart',                             element: <Cart/> },
            { path: 'checkout',                         element: <ProtectedRoute><Checkout/></ProtectedRoute> },
            { path: 'payment/:paymentMethod',           element: <ProtectedRoute><Payment/></ProtectedRoute> },
            { path: 'payment-success',                  element: <PaymentSuccess/> },
            { path: 'checkout/complete/:orderId',       element: <CheckoutComplete/> },
            { path: "search",                           element: <SearchProduct/> },
            { path: "wishlist",                         element: <Wishlist/> },

            // Demo / diagnostics routes — registered ONLY outside production. In a prod
            // build (NODE_ENV === 'production') these paths fall through to NotFound.
            ...(process.env.NODE_ENV !== 'production' ? [
                { path: "address-demo",      element: <AddressDemo/> },
                { path: "diagnostics/maps",  element: <MapsDiagnostics/> },
            ] : []),

            { path: "maps/delivery",                    element: <StoreDeliveryMap/> },
            { path: "my-orders",                        element: <ProtectedRoute><MyOrders/></ProtectedRoute> },
            { path: "track-order",                      element: <OrderLookup/> },
            { path: "orders/:orderId",                  element: <ProtectedRoute><OrderDetails/></ProtectedRoute> },
            { path: "orders/public/:orderNumber",       element: <PublicOrderDetails/> },
            { path: "delivery/track/:jobNumber",        element: <PublicDeliveryTrack/> },
            { path: "tickets/scanner",                  element: <ProtectedRoute><TicketScanner/></ProtectedRoute> },
            { path: "tickets/:code",                    element: <ProtectedRoute><TicketStatus/></ProtectedRoute> },

            // Vendor marketplace (Stage 4): any signed-in user can apply;
            // the portal itself is VENDOR-role only (server re-enforces).
            { path: "become-a-vendor",                  element: <ProtectedRoute><BecomeVendor/></ProtectedRoute> },
            { path: "vendor-portal",                    element: <ProtectedRoute roles={[ROLE.VENDOR]}><VendorPortal/></ProtectedRoute> },

            // Admin panel — gated by role. Previously any logged-in user could load these
            // routes; the data was still server-protected but the UI was reachable. The
            // role list mirrors backend `requireAdmin`. Audit trail tightens further to
            // SUPER_ADMIN-only on both sides.
            {
                path : "admin-panel",
                element : (
                    <ProtectedRoute roles={ADMIN_ROLES}>
                        <AdminPanel/>
                    </ProtectedRoute>
                ),
                children : [
                    { path: "delivery-jobs",   element: <AdminDeliveryJobs/> },
                    { path: "all-users",       element: <AllUsers/> },
                    { path: "all-products",    element: <AllProducts/> },
                    { path: "departments/:vertical", element: <DepartmentDashboard/> },
                    { path: "departments/electronics/warranty-aging", element: <WarrantyAging/> },
                    { path: "departments/groceries/batches", element: <GroceryBatches/> },
                    { path: "departments/fashion/replenishment", element: <FashionReplenishment/> },
                    { path: "attendees",       element: <AttendeesMaster/> },
                    { path: "preorders",       element: <AdminPreorders/> },
                    { path: "notifications/stock-back", element: <StockBackSubscriptions/> },
                    { path: "products/:id/catalog", element: <CatalogManagement/> },
                    { path: "categories", element: <CategoryTreeAdmin/> },
                    { path: "brands", element: <BrandAdmin/> },
                    { path: "suppliers", element: <SupplierAdmin/> },
                    { path: "skus", element: <SkuMaster/> },
                    { path: "inventory", element: <InventoryDashboard/> },
                    { path: "movements", element: <MovementsFeed/> },
                    { path: "pricing", element: <PricingMatrix/> },
                    { path: "stocktakes", element: <StocktakeList/> },
                    { path: "stocktakes/:id", element: <StocktakeSession/> },
                    // V1 stock-management retired (Stage 3) — the SKU-level
                    // Inventory Dashboard (+ bulk upload) replaced it. Old
                    // bookmarks land on the dashboard instead of a 404.
                    { path: "stock-management", element: <InventoryDashboard/> },
                    { path: "vendors",         element: <AdminVendors/> },
                    { path: "all-orders",      element: <AllOrders/> },
                    { path: "audit-trail",     element: <ProtectedRoute roles={SUPER_ADMIN_ONLY}><AuditTrail/></ProtectedRoute> },
                    { path: "sms-log",         element: <SmsLog/> },
                    { path: "store-settings",  element: <AdminStoreSettings/> },
                    { path: "analytics",       element: <AdminAnalytics/> },
                    { path: "banners",         element: <AdminBanners/> },
                ],
            },

            { path : "*", element : <NotFound/> },
        ],
    },
], {
    // Opt in to React Router v7 behaviour now, while still on v6 — this silences the
    // "Future Flag Warning" console messages and means the eventual v7 upgrade is a
    // version bump rather than a behaviour change.
    //
    // Only v7_startTransition actually changes anything here: router state updates are
    // wrapped in React.startTransition, so navigating to a lazy() route keeps the current
    // page on screen until its chunk resolves instead of flashing the <Suspense> fallback
    // (RouteLoading) in App.js. The other four are no-ops for this app — we use no
    // loaders/actions, no useFetcher/useSubmit/<Form>, and the only splat route is the
    // leaf NotFound with no relative links beneath it.
    future: {
        v7_startTransition: true,
        v7_relativeSplatPath: true,
        v7_fetcherPersist: true,
        v7_normalizeFormMethod: true,
        v7_partialHydration: true,
        v7_skipActionErrorRevalidation: true,
    },
})


export default router
